import React, { useEffect, useState } from 'react'
import Typography from '../../components/Typography'
import { useTranslation } from '../../utils/localization'
import ArticleCard from '../../components/ArticleCard'
import BlogArticle from '../../components/BlogArticle'
import Pagination from '../../components/BlogArticle/pagination'
import {
  useSearchParamValue,
  useSearchParamMutation,
} from '../../utils/searchParams'
import { navigate } from 'vike/client/router'
import Button from '../../components/Button'
import { useAuth } from '../../utils/platform_auth'
import DeveloperMenu from '../../components/BlogArticle/developer-menu'
import BlogController from '../../components/BlogArticle/controller'
import CategoryList from '../../components/BlogArticle/category-list'
import { Spinner } from '../../components/Spinner'
import { useData } from 'vike-react/useData'
import NoJavascript from '../../components/NoJavascript'
import { SEARCH_QUERY_FLAGS } from '../../utils/routing.js'

let lastPage = 0

function Page() {
  const t = useTranslation()
  const [articles, setArticles] = useState(-1)
  const articleID = useSearchParamValue('article')
  const [categoryID, setCategoryID] = useState(useSearchParamValue('category'))
  const data = useData()
  const {
    add: addSearchParam,
    remove: removeSearchParam,
  } = useSearchParamMutation()

  const [totalPages, setTotalPages] = useState(1)
  const pageParam = useSearchParamValue('page')
  const [currentPage, setCurrentPage] = useState(pageParam || 1)
  const purchaseSuccess = useSearchParamValue('success') !== undefined
  const auth = useAuth()
  const isDeveloper = () => {
    return auth && auth.email === 'demo@rilagames.com'
  }

  const controller = BlogController()

  // Load the article if the articleID is present
  useEffect(() => {
    // Get the search param article from the URL
    const id = window.location.search.split('article=')[1]
    if (!id) return
    controller.loadArticle(id)
  }, [articleID])

  useEffect(() => {
    if (pageParam === 'refresh') {
      setCurrentPage(lastPage || 1)
    }
  }, [pageParam])

  useEffect(() => {
    // Now at article page
    if (articleID) return

    if (!purchaseSuccess && (!pageParam || pageParam === 'refresh')) {
      // First remove the ?page=refresh from the history
      setTimeout(() => {
        navigate('/blog?page=' + (lastPage || 1), {
          overwriteLastHistoryEntry: true,
        })
      }, 20)
      return
    }

    if (currentPage) controller.updateQueryParams({ page: currentPage })

    lastPage = parseInt(currentPage)
    setTotalPages(data.totalPages)
    setArticles(data.articles)
  }, [purchaseSuccess, currentPage, pageParam, articleID, categoryID])

  const handlePageChange = (page) => {
    setCurrentPage(parseInt(page))
    navigate(addSearchParam(SEARCH_QUERY_FLAGS.PAGE, page))
  }

  const handleSelectCategory = (category) => {
    // Handle category selection
    setCategoryID(category.categoryID)
    controller.updateQueryParams({ page: 1 })

    if (category.categoryID === null) {
      navigate(removeSearchParam(SEARCH_QUERY_FLAGS.CATEGORY))
      window.location.reload()
      return
    }
    navigate(addSearchParam(SEARCH_QUERY_FLAGS.CATEGORY, category.categoryID))
    window.location.reload()
  }

  return (
    <div className="w-full flex">
      {isDeveloper() && <DeveloperMenu controller={controller} />}

      {/* Article Content */}
      <div className="flex-1">
        {articleID ? (
          <BlogArticle controller={controller} />
        ) : (
          <div>
            {/* Header */}
            <div className="relative bg-[#802e5f] w-full h-[180px] flex flex-col items-center justify-center gap-[20px]">
              <div
                className="flex flex-col max-w-[80%] sm:max-w-[560px] md:w-full items-center justify-center gap-[12px] mx-auto"
                style={{ zIndex: 10 }}
              >
                <Typography
                  variant="h1"
                  case="center"
                  className="uppercase text-center text-white"
                >
                  {t`Blog`}
                </Typography>

                <Typography
                  case="center"
                  className="max-w-[80%] text-sm sm:text-base md:text-lg text-center text-white font-medium"
                >
                  {t`Tips about translation process, file management, and more`}
                </Typography>
              </div>

              {/* Abstract background */}
              <div
                className="absolute w-full inset-0 overflow-hidden"
                style={{ zIndex: 0 }}
              >
                {/* Blue rectangle*/}
                <div className="absolute w-[800px] h-2/3 top-[-25%] md:top-0 left-[-65%] sm:left-[-45%] md:left-[5%] bg-gradient-to-l from-[#3548dc] to-[#2c7af7] opacity-70 transform -rotate-45"></div>
                {/* Red rectangle*/}
                <div className="absolute w-[1350px] h-3/4 bottom-[100px] right-[-4%] bg-gradient-to-r from-[#592f66] to-[#ff4d4f] opacity-70 transform rotate-45"></div>
              </div>
            </div>

            {/* Sidebar with Categories */}
            <div className="flex flex-col md:flex-row gap-8 max-w-[950px] lg:max-w-[1200px] mx-auto">
              {articles != -1 && (
                <CategoryList
                  categoryID={categoryID}
                  controller={controller}
                  onSelectCategory={handleSelectCategory}
                />
              )}

              {/* Articles */}
              {articles != -1 && articles?.length > 0 && (
                <div className="flex flex-col w-full">
                  <div className="w-full grid grid-cols-1 md:grid-cols-2 max-w-[580px] sm:max-w-[600px] md:max-w-[950px] lg:max-w-[1200px] gap-4 overflow-x-hidden  px-4 md:px-0 pt-[70px] pb-[40px] md:py-[70px] lg:py-[20px] mx-auto">
                    {articles.map((article) => (
                      <ArticleCard key={article.articleID} {...article} />
                    ))}
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
              {/* No articles found section */}
              {articles != -1 && articles?.length === 0 && (
                <div className="mx-auto flex flex-col justify-center gap-4 mt-20">
                  <Typography variant="subtitle" case="center">
                    {t`No articles found`}
                  </Typography>
                  <Button
                    variant="viewer_controls"
                    onClick={() => {
                      handleSelectCategory({ categoryID: null })
                    }}
                  >{t`Show all articles`}</Button>
                </div>
              )}
              <NoJavascript />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Page
